import { Component } from '@theme/component';

/**
 * Countdown Timer Component
 * Supports both fixed time and recurring countdown modes
 */
class CountdownTimer extends Component {
  static MS_IN_SEC = 1000;
  static MS_IN_MIN = 60 * CountdownTimer.MS_IN_SEC;
  static MS_IN_HRS = 60 * CountdownTimer.MS_IN_MIN;
  static MS_IN_DAY = 24 * CountdownTimer.MS_IN_HRS;

  static UNITS = ['day', 'hour', 'minute', 'second'];

  #intervalId = null;
  #elements = {};
  #labelElements = {};
  #labelTexts = {};
  #targetTime = null;
  #startTime = null;
  #resizeObserver = null;
  #resizeTimeout = null;

  get #type() {
    return this.dataset.type || 'fixed_time';
  }

  get #duration() {
    return this.dataset.duration;
  }

  get #dueDate() {
    return this.dataset.dueDate;
  }

  get #loop() {
    return this.dataset.loop === 'true';
  }

  get #shouldAddLeadingZero() {
    return this.dataset.leadingZero !== 'false';
  }

  get #shouldHideBlockTimeEnd() {
    return this.dataset.hideBlockTimeEnd === 'true';
  }

  connectedCallback() {
    super.connectedCallback();
    this.#clearInterval();
    this.#init();
    this.#attachEventListeners();
    this.#setupResizeObserver();
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    
    if (this.boundHandleCountdownEnd) {
      this.removeEventListener('countdown:end', this.boundHandleCountdownEnd);
    }
    
    this.#clearInterval();
    this.#cleanupResizeObserver();
  }

  /** Attach event listeners */
  #attachEventListeners() {
    this.boundHandleCountdownEnd = this.#handleCountdownEnd.bind(this);
    this.addEventListener('countdown:end', this.boundHandleCountdownEnd);
  }

  /** Handle countdown end event */
  #handleCountdownEnd() {
    if (this.#shouldHideBlockTimeEnd) {
      const block = this.closest('.countdown-timer-block');
      if (block) {
        block.style.display = 'none';
      }
    }
  }

  /** Initialize countdown timer */
  #init() {
    if (!this.#validateAttributes()) return;

    this.#setTargetTime();
    if (!this.#targetTime) return;

    this.#startTime = Date.now();

    if (this.#targetTime > this.#startTime) {
      if (!this.#fetchDomElements()) return;

      this.#startCountdown();
      this.#setValueWidth();
    } else {
      // Already expired at load: dispatch countdown:end (not just hide) so listeners
      // like announcement-bar can remove the slide.
      this.#endCountdown();
    }
  }

  /** Setup ResizeObserver to recalculate width on resize */
  #setupResizeObserver() {
    if (!this.classList.contains('countdown-timer--background')) return;
    
    this.#resizeObserver = new ResizeObserver(() => {
      clearTimeout(this.#resizeTimeout);
      this.#resizeTimeout = setTimeout(() => this.#setValueWidth(), 150);
    });
    this.#resizeObserver.observe(this);
  }

  /** Cleanup ResizeObserver */
  #cleanupResizeObserver() {
    this.#resizeObserver?.disconnect();
    clearTimeout(this.#resizeTimeout);
    this.#resizeObserver = null;
    this.#resizeTimeout = null;
  }

  /** Calculate and set width for countdown values */
  #setValueWidth() {
    if (!this.classList.contains('countdown-timer--background')) return;
    let valueWidth = 0;
    
    for (const unit of CountdownTimer.UNITS) {
      const unitWidth = this.#elements[unit]?.offsetWidth;
      if (unitWidth && unitWidth > valueWidth) {
        valueWidth = unitWidth;
      }
    }
    
    if (valueWidth > 0) {
      this.style.setProperty('--value-width', `${valueWidth}px`);
    }
  }

  /** Validate required data attributes */
  #validateAttributes() {
    if (!this.#type) return false;
    if (this.#type === 'fixed_time' && !this.#dueDate) return false;
    if (this.#type === 'recurring' && !this.#duration) return false;
    return true;
  }

  /** Set target time based on countdown type */
  #setTargetTime() {
    switch (this.#type) {
      case 'fixed_time':
        if (this.#dueDate) {
          const date = new Date(this.#dueDate);
          if (isNaN(date.getTime())) return;
          this.#targetTime = date.getTime();
        }
        break;

      case 'recurring':
        this.#targetTime = this.#getRecurringDueDate().getTime();
        break;
    }
  }

  /** Cache DOM elements for timer display */
  #fetchDomElements() {
    // Reset caches so stale refs don't survive a reconnect/morph that replaced children.
    this.#elements = {};
    this.#labelElements = {};
    this.#labelTexts = {};
    let hasAnyElement = false;

    CountdownTimer.UNITS.forEach((unit) => {
      const container = this.querySelector(`[data-timer-${unit}]`);
      
      if (container) {
        const valueElement = container.querySelector('.countdown-timer__value');
        const labelElement = container.querySelector('.countdown-timer__label');
        
        this.#elements[unit] = valueElement;
        this.#labelElements[unit] = labelElement;
        
        if (valueElement) hasAnyElement = true;
        
        if (labelElement) {
          this.#labelTexts[unit] = {
            singular: labelElement.dataset.s || null,
            plural: labelElement.textContent.trim()
          };
        }
      }
    });

    return hasAnyElement;
  }

  /** Start the countdown interval */
  #startCountdown() {
    if (this.#intervalId) {
      this.#clearInterval();
    }

    const initialRemaining = this.#targetTime - Date.now();
    
    if (initialRemaining <= 0) {
      this.#updateDisplay(0);
      this.#endCountdown();
      return;
    }
    
    this.#updateDisplay(initialRemaining);

    this.#intervalId = setInterval(() => {
      const remainingTime = this.#targetTime - Date.now();

      if (remainingTime <= 0) {
        this.#updateDisplay(0);
        this.#endCountdown();
      } else {
        this.#updateDisplay(remainingTime);
      }
    }, 1000);

    this.classList.remove('hidden');
  }

  /**
   * Updates timer display with remaining time
   * Only updates DOM when values actually change to minimize reflows
   * Handles singular/plural label forms based on value
   * @param {number} remainingTime - Remaining time in milliseconds
   */
  #updateDisplay(remainingTime) {
    const timeComponents = this.#computeTimeComponents(remainingTime);

    CountdownTimer.UNITS.forEach((unit) => {
      const value = timeComponents[unit];
      
      // Update value only when it actually changes
      if (this.#elements[unit]) {
        const newValue = this.#formatTimeComponent(value);
        if (this.#elements[unit].textContent !== newValue) {
          this.#elements[unit].textContent = newValue;
        }
      }
      
      // Update label only when text actually changes
      if (this.#labelElements[unit] && this.#labelTexts[unit]) {
        const { singular, plural } = this.#labelTexts[unit];
        
        if (singular && plural) {
          const newLabel = value === 1 ? singular : plural;

          if (this.#labelElements[unit].textContent !== newLabel) {
            this.#labelElements[unit].textContent = newLabel;
          }
        }
      }
    });
  }

  /**
   * Calculates time components (days, hours, minutes, seconds) from milliseconds
   * @param {number} ms - Time in milliseconds
   * @returns {Object} Object with day, hour, minute, second values
   */
  #computeTimeComponents(ms) {
    return {
      day: Math.floor(ms / CountdownTimer.MS_IN_DAY),
      hour: Math.floor((ms % CountdownTimer.MS_IN_DAY) / CountdownTimer.MS_IN_HRS),
      minute: Math.floor((ms % CountdownTimer.MS_IN_HRS) / CountdownTimer.MS_IN_MIN),
      second: Math.floor((ms % CountdownTimer.MS_IN_MIN) / CountdownTimer.MS_IN_SEC),
    };
  }

  /**
   * Formats time component value with optional leading zero
   * @param {number} value - Time value to format
   * @returns {string} Formatted string (e.g., "05" or "5")
   */
  #formatTimeComponent(value) {
    if (this.#shouldAddLeadingZero && value < 10) {
      return `0${value}`;
    }
    return value.toString();
  }

  /**
   * Ends countdown and dispatches countdown:end event
   * Restarts countdown if loop is enabled, otherwise hides timer if configured
   */
  #endCountdown() {
    this.#clearInterval();

    this.dispatchEvent(new CustomEvent('countdown:end', {
      bubbles: true,
      detail: { 
        type: this.#type,
        duration: this.#duration,
        dueDate: this.#dueDate
      }
    }));

    if (this.#loop) {
      this.#init();
    } else {
      if (this.#shouldHideBlockTimeEnd) {
        this.classList.add('hidden');
      }
    }
  }

  /**
   * Clears countdown interval
   * Prevents memory leaks by cleaning up interval timer
   */
  #clearInterval() {
    if (this.#intervalId) {
      clearInterval(this.#intervalId);
      this.#intervalId = null;
    }
  }

  /**
   * Reset countdown to 00:00:00
   * @public
   */
  reset() {
    this.#clearInterval();
    CountdownTimer.UNITS.forEach((unit) => {
      if (this.#elements[unit]) {
        this.#elements[unit].textContent = '00';
      }
    });
  }

  /**
   * Calculates target date for recurring countdown based on duration
   * Supports daily, weekly, monthly, and hourly intervals
   * @returns {Date} Next occurrence date
   */
  #getRecurringDueDate() {
    const now = new Date();

    switch (this.#duration) {
      case 'every_day':
        return this.#getNextDayDate(now);
      case 'every_week':
        return this.#getNextWeekDate(now);
      case 'every_month':
        return this.#getNextMonthDate(now);
      case 'every_1_hr':
      case 'every_3_hrs':
      case 'every_6_hrs':
      case 'every_12_hrs':
        return this.#getNextHourlyDate(now);
      default:
        return now;
    }
  }

  /**
   * Gets next day at midnight
   * @param {Date} now - Current date
   * @returns {Date} Next day at 00:00:00
   */
  #getNextDayDate(now) {
    const date = new Date(now);
    date.setDate(date.getDate() + 1);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  /**
   * Gets next Monday at midnight (start of next week)
   * @param {Date} now - Current date
   * @returns {Date} Next Monday at 00:00:00
   */
  #getNextWeekDate(now) {
    const date = new Date(now);
    const currentDay = now.getDay();
    
    let daysUntilNextMonday;
    if (currentDay === 0) {
      daysUntilNextMonday = 1;
    } else {
      daysUntilNextMonday = 8 - currentDay;
    }
    
    date.setDate(date.getDate() + daysUntilNextMonday);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  /**
   * Gets next month's first day at midnight
   * @param {Date} now - Current date
   * @returns {Date} First day of next month at 00:00:00
   */
  #getNextMonthDate(now) {
    const date = new Date(now);
    date.setDate(1);
    date.setMonth(date.getMonth() + 1);
    date.setHours(0, 0, 0, 0);
    return date;
  }

  /**
   * Gets next hourly checkpoint based on interval
   * If current hour is past last checkpoint, moves to next day
   * @param {Date} now - Current date
   * @returns {Date} Next hourly checkpoint
   */
  #getNextHourlyDate(now) {
    const intervalHrs = this.#getIntervalHours();
    const startPoints = this.#generateHourlyStartPoints(intervalHrs);
    const date = new Date(now);
    const currentHour = now.getHours();

    for (let i = 0; i < startPoints.length; i++) {
      if (currentHour < startPoints[i]) {
        date.setHours(startPoints[i], 0, 0, 0);
        return date;
      }
    }

    date.setDate(date.getDate() + 1);
    date.setHours(startPoints[0], 0, 0, 0);
    return date;
  }

  /**
   * Gets interval hours from duration string
   * Maps duration values to hour intervals (1, 3, 6, or 12 hours)
   * @returns {number} Interval in hours (default: 1)
   */
  #getIntervalHours() {
    const intervalMap = {
      every_1_hr: 1,
      every_3_hrs: 3,
      every_6_hrs: 6,
      every_12_hrs: 12
    };
    return intervalMap[this.#duration] || 1;
  }

  /**
   * Generates array of hourly start points based on interval
   * Creates checkpoints throughout the day (e.g., [0, 3, 6, 9, 12, 15, 18, 21] for 3-hour intervals)
   * @param {number} intervalHrs - Interval in hours
   * @returns {number[]} Array of hour values (0-23)
   */
  #generateHourlyStartPoints(intervalHrs) {
    const startPoints = [];
    for (let i = 0; i < 24; i += intervalHrs) {
      startPoints.push(i);
    }
    return startPoints;
  }
}

if (!customElements.get('countdown-timer')) {
  customElements.define('countdown-timer', CountdownTimer);
}
